/**
 * LoadingSpinner Component
 * 
 * A reusable loading spinner component with consistent admin styling.
 * Used across admin screens to indicate loading states during data fetching,
 * tab switching, and other async operations.
 * 
 * Features:
 * - Configurable spinner size (small/large)
 * - Customizable loading message
 * - Optional message display toggle
 * - Consistent admin theme colors
 * - Centered layout with proper spacing
 * 
 * Usage:
 * ```tsx
 * <LoadingSpinner 
 *   message="Loading bulletins..." 
 *   size="large" 
 *   showMessage={true} 
 * />
 * ```
 */
import React from 'react';
import { View, ActivityIndicator, Text, StyleSheet } from 'react-native';
import { PRIMITIVE_COLORS } from '@/constants/residents/residentColors';
import { useThemeColors } from '@/hooks/shared/useThemeColors';

interface AdminLoadingSpinnerProps {
  message?: string;
  size?: 'small' | 'large';
  showMessage?: boolean;
  variant?: 'admin' | 'resident';
  textColor?: string;
  spinnerColor?: string;
}

export const LoadingSpinner: React.FC<AdminLoadingSpinnerProps> = ({
  message = 'Loading...',
  size = 'large',
  showMessage = true,
  variant = 'admin',
  textColor,
  spinnerColor: spinnerColorProp,
}) => {
  const { semantic } = useThemeColors();
  const spinnerColor = spinnerColorProp ?? (variant === 'resident' ? semantic.interactive.primary : PRIMITIVE_COLORS.resioBlueLight);
  const messageColor = textColor ?? (variant === 'resident' ? semantic.text.primary : semantic.text.tertiary);
  return (
    <View style={styles.container}>
      <ActivityIndicator 
        size={size} 
        color={spinnerColor} 
        style={styles.spinner}
      />
      {showMessage && (
        <Text style={[styles.message, { color: messageColor }]}>{message}</Text>
      )}
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    padding: 24,
  },
  spinner: {
    marginBottom: 16,
  },
  message: {
    fontSize: 16,
    textAlign: 'center',
    fontWeight: '500',
  },
});
